export interface GroupDiscountTier {
  minParticipants: number
  fee: number
}

export interface GroupDiscountPrice {
  fee: number
  tier: GroupDiscountTier | null
}

type RawRecord = Record<string, unknown>

const asRecord = (value: unknown): RawRecord | null =>
  value && typeof value === 'object' ? value as RawRecord : null

const toFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined) return null
  if (typeof value === 'string' && value.trim() === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

// Same rules as the session API: an all-digit option code wins, otherwise the first number in
// the description, then in the code ("10 or more" -> 10).
export const parseGroupDiscountMinParticipants = (code: unknown, description: unknown): number | null => {
  const trimmedCode = typeof code === 'string' ? code.trim() : code == null ? '' : String(code).trim()
  if (/^\d+$/.test(trimmedCode)) {
    const numericCode = Number(trimmedCode)
    return numericCode > 0 ? numericCode : null
  }

  for (const text of [description, trimmedCode]) {
    if (typeof text !== 'string') continue
    const match = text.match(/\d+/)
    const parsed = match ? Number(match[0]) : 0
    if (parsed > 0) return parsed
  }

  return null
}

const roundCurrency = (value: number): number => Math.round(value * 100) / 100

export const normalizeGroupDiscountTiers = (rows: unknown): GroupDiscountTier[] => {
  if (!Array.isArray(rows)) return []

  const byThreshold = new Map<number, number>()
  for (const row of rows) {
    const record = asRecord(row)
    if (!record) continue

    const fee = toFiniteNumber(record.fee)
    if (fee === null || fee < 0) continue

    const explicitMin = toFiniteNumber(record.minParticipants)
    const minParticipants = explicitMin !== null
      ? (Number.isInteger(explicitMin) && explicitMin > 0 ? explicitMin : null)
      : parseGroupDiscountMinParticipants(record.code, record.description)
    if (minParticipants === null) continue

    const existing = byThreshold.get(minParticipants)
    if (existing === undefined || fee < existing) byThreshold.set(minParticipants, roundCurrency(fee))
  }

  return [...byThreshold.entries()]
    .map(([minParticipants, fee]) => ({ minParticipants, fee }))
    .sort((a, b) => a.minParticipants - b.minParticipants)
}

// Prefers the API's normalized group_discounts and falls back to the synced discounts array.
export const getSessionGroupDiscountTiers = (sessionDetails: unknown): GroupDiscountTier[] => {
  const record = asRecord(sessionDetails)
  if (!record) return []
  if (Array.isArray(record.group_discounts)) return normalizeGroupDiscountTiers(record.group_discounts)
  if (Array.isArray(record.groupDiscounts)) return normalizeGroupDiscountTiers(record.groupDiscounts)
  return normalizeGroupDiscountTiers(record.discounts)
}

export const resolveGroupDiscountTier = (
  tiers: readonly GroupDiscountTier[] | undefined,
  participantCount: number,
): GroupDiscountTier | null => {
  let applied: GroupDiscountTier | null = null
  for (const tier of tiers ?? []) {
    if (participantCount >= tier.minParticipants && (!applied || tier.minParticipants > applied.minParticipants)) {
      applied = tier
    }
  }
  return applied
}

// A tier only applies when it is cheaper than the participant's regular, early, or membership price.
export const getGroupDiscountPrice = (
  baseFee: number,
  tiers: readonly GroupDiscountTier[] | undefined,
  participantCount: number,
): GroupDiscountPrice => {
  const tier = resolveGroupDiscountTier(tiers, participantCount)
  return tier && tier.fee < baseFee ? { fee: tier.fee, tier } : { fee: baseFee, tier: null }
}

export const getGroupDiscountTierMax = (
  tiers: readonly GroupDiscountTier[],
  index: number,
): number | null => {
  const next = tiers[index + 1]
  return next ? next.minParticipants - 1 : null
}
